import { PageHeader } from '@ant-design/pro-layout';
import { Button, DatePicker, Table } from 'antd';
import Title from 'antd/es/typography/Title';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { DATE_FORMAT, FormatDate } from '../../../../constants';
import { StaffFinanceDetailContainerGlTransactionSummaryFieldsFragment } from '../../../../generated';
import { downloadCsv, jsonToCsv } from '../../../shared/json-to-csv';

interface FinanceDetailProps {
  data: StaffFinanceDetailContainerGlTransactionSummaryFieldsFragment[];
  setFilterDates: (dates: { startDate: string | null; endDate: string | null }) => void;
  filterDates: { startDate: string | null; endDate: string | null } | null;
}

export const FinanceDetail: React.FC<FinanceDetailProps> = (props) => {
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(props.filterDates?.startDate ? dayjs(props.filterDates.startDate) : null);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(props.filterDates?.endDate ? dayjs(props.filterDates.endDate) : null);

  const sortedData = useMemo(() => {
    return [...(props.data ?? [])].sort((a, b) => (dayjs(a.transactionReference?.completedAt).isBefore(dayjs(b.transactionReference?.completedAt)) ? 1 : -1));
  }, [props.data]);

  const handleStartDateChange = (date: dayjs.Dayjs | null) => {
    setStartDate(date);
    if (endDate?.isBefore(date, 'day')) {
      console.log('End date is before start date');
      setEndDate(null);
    }
  };

  const handleEndDateChange = (date: dayjs.Dayjs | null) => {
    setEndDate(date);
  };

  const disabledEndDate = (current: dayjs.Dayjs) => {
    return startDate ? current.isBefore(startDate, 'day') : false;
  };

  const handleDateFilter = () => {
    props.setFilterDates({
      startDate: startDate ? startDate.format('YYYY-MM-DD') : null,
      endDate: endDate ? endDate.format('YYYY-MM-DD') : null
    });
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    props.setFilterDates({ startDate: null, endDate: null });
  };

  return (
    <>
      <PageHeader>
        <div className="flex flex-col" style={{ marginBottom: '30px' }}>
          <div id="leftTitle" style={{ display: 'inline', float: 'left' }}>
            <Title level={3} style={{ display: 'inline' }}>
              Finance Detail
            </Title>
          </div>
        </div>
      </PageHeader>
      <div className="flex justify-between items-center">
        <div className="self-start my-4 flex">
          <div className="mb-2">
            <span className="mr-2">Start Date:</span>
            <DatePicker
              format={(value) => value.format(DATE_FORMAT).toUpperCase()}
              placeholder="Start Date"
              className="mr-2"
              onChange={handleStartDateChange}
              value={startDate}
            />
          </div>
          <div className="mb-2">
            <span className="mr-2">End Date:</span>
            <DatePicker
              format={(value) => value.format(DATE_FORMAT).toUpperCase()}
              placeholder="End Date"
              className="mr-2"
              onChange={handleEndDateChange}
              value={endDate}
              disabledDate={disabledEndDate}
            />
          </div>
          <div className="inline-flex gap-2">
            <Button type="primary" onClick={handleDateFilter}>
              Filter
            </Button>
            <Button onClick={handleReset}>Reset</Button>
          </div>
        </div>

        <div>
          <Button
            type="primary"
            onClick={() => {
              const csvData = jsonToCsv(props.data);
              downloadCsv(csvData, 'finance-detail.csv');
            }}
            disabled={!props?.data || props.data.length === 0}
          >
            Download CSV File
          </Button>
        </div>
      </div>

      <Table<StaffFinanceDetailContainerGlTransactionSummaryFieldsFragment>
        dataSource={sortedData}
        size="middle"
        scroll={{ x: 'calc(700px + 50%)', y: 'calc(100vh - 300px)' }}
        className="w-full border border-[#f0f0f0] rounded-[10px] p-[15px] max-w-full border-solid"
        rowHoverable={false}
        rowKey={(record) => record.id}
      >
        <Table.Column title="Transaction Type" dataIndex="transactionType" key="transactionType" />
        <Table.Column title="Amount" dataIndex="amount" key="amount" render={(amount: number, record: StaffFinanceDetailContainerGlTransactionSummaryFieldsFragment) => record.transactionReference?.referenceId?.includes('AHP') ? amount : amount * -1} />
        <Table.ColumnGroup title="Transaction Reference">
          <Table.Column title="Vendor" dataIndex={['transactionReference', 'vendor']} key="vendor" />
          <Table.Column title="Reference ID" dataIndex={['transactionReference', 'referenceId']} key="referenceId" />
          <Table.Column
            title="Completed At"
            dataIndex={['transactionReference', 'completedAt']}
            key="completedAt"
            render={(text: string) => FormatDate(text)}
          />
        </Table.ColumnGroup>
        <Table.Column title="Applicant" dataIndex={['applicant', 'displayName']} key="applicant" />
        <Table.Column title="Case" dataIndex="case" key="case" />
        <Table.Column title="Case Type" dataIndex="caseType" key="caseType" />
        <Table.Column title="Reason" dataIndex="reason" key="reason" />
        <Table.ColumnGroup title="Finance Reference">
          <Table.Column title="Debit Account" dataIndex={['financeReference', 'debitGlAccount']} key="debitGlAccount" />
          <Table.Column title="Credit Account" dataIndex={['financeReference', 'creditGlAccount']} key="creditGlAccount" />
        </Table.ColumnGroup>
      </Table>
    </>
  );
};
